import type { SubscriptionWarning } from 'src/sections/settings/billing/types/billing';
import type {
  InvoiceDto,
  PlanLimits,
  PlanWarning,
  PlanGrantDto,
  UsageSummary,
  CreditPricingDto,
  PaymentMethodDto,
  SubscriptionSummaryDto,
  CreditBalanceResponseDto,
  OrgBillingStatusResponse,
} from 'src/lib/api';

import { useMemo, useContext, useCallback, createContext } from 'react';

import { endpoints, useApiQuery } from 'src/lib/api';
import { useOrganisation } from 'src/organisation/context/provider';

import { useCan } from 'src/auth/hooks/use-role';

import { deriveSubscriptionWarning } from './helpers';

type BillingContextType = {
  orgId: string | null;
  orgStatus: OrgBillingStatusResponse | null;
  subscription: SubscriptionSummaryDto | null;
  invoices: InvoiceDto[] | null;
  paymentMethod: PaymentMethodDto | null;
  serverCurrentPlan: string | null;
  isDevAccount: boolean;
  planLimits: PlanLimits | null;
  currentUsage: UsageSummary | null;
  warnings: PlanWarning[] | null;
  creditBalances: CreditBalanceResponseDto[];
  creditPricing: CreditPricingDto | null;
  /** Free-months grant currently running, from a coupon or a support comp. */
  activePlanGrant: PlanGrantDto | null;
  /** Whether credit top-ups are allowed right now. Decided server-side. */
  canBuyPack: boolean;
  subscriptionWarning: SubscriptionWarning;
  isBillingLoading: boolean;
  billingError: unknown;
  revalidateBilling: () => Promise<void>;
  revalidateOrgStatus: () => Promise<unknown>;
};

const BillingContext = createContext<BillingContextType | null>(null);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { organizationId } = useOrganisation();
  const orgId = organizationId ? String(organizationId) : null;
  // Invoices and the payment method are Administrator-only; other roles only need the plan status.
  const canSeeBilling = useCan('settings.billing');
  const statusRequest = orgId ? {} : null;
  const billingRequest = orgId && canSeeBilling ? {} : null;

  const {
    data: orgStatus,
    isLoading: isStatusLoading,
    error: statusError,
    mutate: mutateStatus,
  } = useApiQuery(endpoints.billing.getOrgBillingStatus, statusRequest, {
    revalidateOnMount: true,
    revalidateOnFocus: true,
    revalidateIfStale: true,
    dedupingInterval: 0,
  });

  const {
    data: invoicesData,
    isLoading: isInvoicesLoading,
    mutate: mutateInvoices,
  } = useApiQuery(endpoints.billing.getInvoices, billingRequest, {
    onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 401 || status === 403) return;
      if (retryCount >= 2) return;
      setTimeout(() => revalidate({ retryCount }), 3000);
    },
  });

  const {
    data: paymentMethodData,
    mutate: mutatePaymentMethod,
  } = useApiQuery(endpoints.billing.getPaymentMethod, billingRequest, {
    onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 400 || status === 401 || status === 403) return;
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 3000);
    },
  });

  const revalidateBilling = useCallback(async () => {
    await Promise.all([mutateStatus(), mutateInvoices(), mutatePaymentMethod()]);
  }, [mutateStatus, mutateInvoices, mutatePaymentMethod]);

  const value = useMemo<BillingContextType>(() => {
    const subscription = orgStatus?.Subscription ?? null;
    const paymentMethod = paymentMethodData?.PaymentMethod ?? null;

    return {
      orgId,
      orgStatus: orgStatus ?? null,
      subscription,
      invoices: invoicesData?.Invoices ?? null,
      paymentMethod,
      serverCurrentPlan: orgStatus?.Organization.CurrentPlan || null,
      isDevAccount: orgStatus?.Organization.IsDevAccount ?? false,
      planLimits: orgStatus?.PlanLimits ?? null,
      currentUsage: orgStatus?.CurrentUsage ?? null,
      warnings: orgStatus?.Warnings ?? null,
      creditBalances: orgStatus?.CreditBalances ?? [],
      creditPricing: orgStatus?.CreditPricing ?? null,
      activePlanGrant: orgStatus?.PlanGrant ?? null,
      canBuyPack: orgStatus?.CanBuyPack ?? false,
      subscriptionWarning: deriveSubscriptionWarning(subscription, paymentMethod),
      isBillingLoading: isStatusLoading || isInvoicesLoading,
      billingError: statusError ?? null,
      revalidateBilling,
      revalidateOrgStatus: mutateStatus,
    };
  }, [
    orgId,
    orgStatus,
    invoicesData,
    paymentMethodData,
    isStatusLoading,
    isInvoicesLoading,
    statusError,
    revalidateBilling,
    mutateStatus,
  ]);

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
